/**
 * Hero Component
 * 
 * Main landing section featuring full-screen background, value proposition,
 * and primary call-to-action buttons.
 * 
 * DESIGN STANDARDS:
 * - Full viewport height with min-height constraint
 * - Background image with no overlay (image has built-in contrast)
 * - Two CTA buttons: Primary (solid) + Secondary (bright white)
 * - Responsive text sizing and button stacking
 * - Consistent padding for navbar clearance (pt-20 md:pt-28)
 * 
 * @component
 */

import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/newhero.jpg";
import { scrollToServices } from "@/utils/scroll";

const Hero = () => {
  const navigate = useNavigate();
  
  const handleGetQuote = useCallback(() => {
    navigate('/contact');
  }, [navigate]);

  return (
    <section 
      className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
      aria-labelledby="hero-heading"
    >
      {/* Background Image - Optimized for SEO */}
      <img
        src={heroImage}
        alt="OceanNova maritime logistics operations - cargo ships at sea showcasing global shipping and freight forwarding services"
        className="absolute inset-0 w-full h-full object-cover"
        width="1920"
        height="1080"
        loading="eager"
        fetchPriority="high"
      />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 pt-12 md:pt-20">
        <div className="max-w-4xl animate-fade-in">
          <h1 
            id="hero-heading"
            className="text-3xl md:text-5xl lg:text-6xl font-medium leading-[1.3] mb-8 font-newsreader text-white"
          >
            Different Perspective
          </h1>
          <p className="text-lg md:text-xl mb-10 max-w-2xl leading-relaxed text-white/95">
            Combining 25 years of proven expertise in ship chartering, port forwarding services, 
            port management, port infrastructure projects, and building complete supply chains with a fresh 
            approach to process digitization. We deliver cutting-edge logistics solutions 
            built on decades of industry excellence.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              size="lg" 
              onClick={handleGetQuote}
              className="shadow-lg hover:shadow-xl transition-all"
              aria-label="Get a quote for logistics services"
            >
              Get a Quote
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Button>
            <Button 
              size="lg" 
              onClick={scrollToServices}
              className="bg-white hover:bg-white/90 text-primary font-medium shadow-md hover:shadow-lg transition-all"
              aria-label="Explore our logistics services"
            >
              Our Services
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(Hero);
